import type { UserRole } from './auth.js';
import type { AssetStatus, PublicAsset, PublicTote } from './catalog.js';
import type { PublicEvent } from './events.js';

/** Replacement requests (F-07, ADR-010, DATA-MODEL.md). Internal tickets, not vendor RMAs. */

export const REPLACEMENT_KINDS = ['missing', 'broken'] as const;
export type ReplacementKind = (typeof REPLACEMENT_KINDS)[number];

export const REPLACEMENT_KIND_LABELS: Record<ReplacementKind, string> = {
  missing: 'Missing',
  broken: 'Broken',
};

export const REPLACEMENT_STATUSES = ['open', 'in_progress', 'resolved', 'cancelled'] as const;
export type ReplacementStatus = (typeof REPLACEMENT_STATUSES)[number];

export const REPLACEMENT_STATUS_LABELS: Record<ReplacementStatus, string> = {
  open: 'Open',
  in_progress: 'In progress',
  resolved: 'Resolved',
  cancelled: 'Cancelled',
};

export const OPEN_REPLACEMENT_STATUSES: readonly ReplacementStatus[] = ['open', 'in_progress'];

export function isReplacementKind(value: unknown): value is ReplacementKind {
  return typeof value === 'string' && (REPLACEMENT_KINDS as readonly string[]).includes(value);
}

export function isReplacementStatus(value: unknown): value is ReplacementStatus {
  return typeof value === 'string' && (REPLACEMENT_STATUSES as readonly string[]).includes(value);
}

export function isOpenReplacementStatus(status: ReplacementStatus): boolean {
  return status === 'open' || status === 'in_progress';
}

/** Admin and equipment admin run the replacement workflow (docs/ROLES.md). */
export function roleCanManageReplacements(role: UserRole): boolean {
  return role === 'admin' || role === 'equipment_admin';
}

export interface PublicReplacementActor {
  userId: string | null;
  codeId: string | null;
  name: string;
  email: string | null;
}

export interface PublicReplacementRequest {
  id: string;
  assetId: string | null;
  asset: PublicAsset | null;
  toteId: string | null;
  tote: PublicTote | null;
  loadoutId: string | null;
  eventId: string | null;
  event: Pick<PublicEvent, 'id' | 'name' | 'startDate' | 'endDate'> | null;
  inventoryLineId: string | null;
  kind: ReplacementKind;
  description: string;
  status: ReplacementStatus;
  requestedBy: PublicReplacementActor;
  resolvedBy: PublicReplacementActor | null;
  resolvedAt: string | null;
  resolutionNotes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ReplacementListResponse {
  replacements: PublicReplacementRequest[];
}

export interface ReplacementResponse {
  replacement: PublicReplacementRequest;
}

export type OpenIssueKind = 'replacement' | 'asset';

export interface OpenIssue {
  kind: OpenIssueKind;
  id: string;
  title: string;
  detail: string;
  href: string;
  status: ReplacementStatus | AssetStatus;
}

export interface OpenIssuesResponse {
  issues: OpenIssue[];
}
